import { useEffect, useState } from "react";
import { mustGetElementById, mustQuerySelectorAtIndex } from "./helpers";
import { useProxyErrors } from "./useProxyErrors";
import { useProxyError } from "./useProxyError";
import { useProxyAction } from "./useProxyAction";

const getVerificationCodeInput = () => mustGetElementById<HTMLInputElement>('verificationCode');

const getSendVerificationCodeButton = () => mustGetElementById<HTMLInputElement>('verifyEmailControl_but_send_code');
const getVerifyCodeButton = () => mustGetElementById<HTMLInputElement>('verifyEmailControl_but_verify_code');
const getSendNewCodeButton = () => mustGetElementById<HTMLInputElement>('verifyEmailControl_but_send_new_code');
const getChangeEmailButton = () => mustGetElementById<HTMLInputElement>('verifyEmailControl_but_change_claims');
const getContinueButton = () => mustGetElementById<HTMLButtonElement>('continue');
const getCancelButton = () => mustGetElementById<HTMLButtonElement>('cancel');

export enum ItemLevelErrorIndexes {
  Unknown = 0,
  Email = 1,
  Code = 2,
}
const getCodeError = () =>
  mustQuerySelectorAtIndex('#api .error.itemLevel', ItemLevelErrorIndexes.Code, 'code error');
const getPageLevelErrors = () => Array.from<HTMLElement>(document.querySelectorAll('#api .error.pageLevel'));
const getVerificationError = () => mustGetElementById<HTMLDivElement>('verifyEmailControl_error_message');
const getEmailSuccess = () => mustGetElementById<HTMLDivElement>('verifyEmailControl_success_message');

export enum PageLevelErrorIndexes {
  RequiredFieldMissing = 0,
  OneOrMoreIncorrectFields = 1,
  ServerError = 2,
}

export function useVerifyEmailProxy({ verificationCode } : {
  verificationCode: string
}) {
  // fields
  useEffect(() => {
    getVerificationCodeInput().value = verificationCode;
  }, [verificationCode]);

  // actions
  const sendCodeAction = useProxyAction(getSendVerificationCodeButton);
  const verifyCodeAction = useProxyAction(getVerifyCodeButton);
  const sendNewCodeAction = useProxyAction(getSendNewCodeButton);
  const changeEmailAction = useProxyAction(getChangeEmailButton);
  const continueAction = useProxyAction(getContinueButton);
  const cancelAction = useProxyAction(getCancelButton);

  // errors
  const codeError = useProxyError(getCodeError);
  const pageLevelError = useProxyErrors(getPageLevelErrors);
  const verificationError = useProxyError(getVerificationError);

  const successMessage = useProxyError(getEmailSuccess);
  useEffect(() => {
    if (successMessage !== '') continueAction.act();
  }, [continueAction, successMessage]);

  const [readOnlyEmail, setReadOnlyEmail] = useState('');
  useEffect(() => {
    const emailElement = document.getElementById("readOnlyEmail") as HTMLInputElement;
    const address = emailElement?.value ?? '';
    setReadOnlyEmail(address);
  }, []);

  return {
    sendCodeAction, verifyCodeAction, sendNewCodeAction, changeEmailAction, continueAction, cancelAction,
    verificationError, pageLevelError, codeError, readOnlyEmail
  };
}
